import { Controller } from "@hotwired/stimulus";

/**
 * Nested Duplicate Controller
 *
 * Duplicates a nested-attrs-field item within a form by cloning its DOM,
 * re-keying field names/IDs to a new unique index, and marking the clone
 * as a new record. Works alongside forms--nested-attrs without modifying it.
 *
 * Place this controller on the same element as forms--nested-attrs.
 */
export default class extends Controller {
  duplicate(event) {
    event.preventDefault();

    const original = event.target.closest(".nested-attrs-field");
    if (!original) return;

    const clone = original.cloneNode(true);
    const newIndex = new Date().getTime();

    this.rekeyFields(clone, original, newIndex);
    this.copyFormValues(original, clone);
    this.markAsNewRecord(clone, newIndex);
    this.clearChildSections(clone);

    // Insert the clone after the original
    original.after(clone);
  }

  // Replace the original's field name index with a new unique index
  rekeyFields(clone, original, newIndex) {
    // Find the original index from any input's name attribute
    // Names look like: nu_content_page[nu_page_content_sections_attributes][INDEX][field]
    const sampleInput = original.querySelector("input[name*='[id]']");
    if (!sampleInput) return;

    const nameMatch = sampleInput.name.match(
      /\[nu_page_content_sections_attributes\]\[([^\]]+)\]/,
    );
    if (!nameMatch) return;

    const originalIndex = nameMatch[1];
    const escapedIndex = originalIndex.replace(/[.*+?^${}()|[\]\\]/g, "\\$&");
    const indexPattern = new RegExp(escapedIndex, "g");

    // Replace in all name and id attributes, and in for attributes on labels
    clone.querySelectorAll("[name], [id], [for]").forEach((el) => {
      if (el.name) el.name = el.name.replace(indexPattern, newIndex);
      if (el.id) el.id = el.id.replace(indexPattern, newIndex);
      if (el.htmlFor) el.htmlFor = el.htmlFor.replace(indexPattern, newIndex);
    });
  }

  // cloneNode copies the HTML but not the live DOM state of form elements
  copyFormValues(original, clone) {
    // Copy text input values (cloneNode copies the HTML attribute, not
    // the live property, so user-typed values would be lost)
    const originalTexts = original.querySelectorAll("input[type='text']");
    const cloneTexts = clone.querySelectorAll("input[type='text']");
    originalTexts.forEach((input, i) => {
      if (cloneTexts[i]) cloneTexts[i].value = input.value;
    });

    // Copy select values
    const originalSelects = original.querySelectorAll("select");
    const cloneSelects = clone.querySelectorAll("select");
    originalSelects.forEach((sel, i) => {
      if (cloneSelects[i]) cloneSelects[i].value = sel.value;
    });

    // Copy textarea values
    const originalTextareas = original.querySelectorAll("textarea");
    const cloneTextareas = clone.querySelectorAll("textarea");
    originalTextareas.forEach((ta, i) => {
      if (cloneTextareas[i]) cloneTextareas[i].value = ta.value;
    });

    // Copy checkbox states
    const originalCheckboxes = original.querySelectorAll(
      "input[type='checkbox']",
    );
    const cloneCheckboxes = clone.querySelectorAll("input[type='checkbox']");
    originalCheckboxes.forEach((cb, i) => {
      if (cloneCheckboxes[i]) cloneCheckboxes[i].checked = cb.checked;
    });
  }

  markAsNewRecord(clone, newIndex) {
    // Mark as new record so the remove action deletes from DOM instead of flagging _destroy
    clone.dataset.newRecord = "true";

    // Clear the id field so Rails creates a new record on save
    const idInput = clone.querySelector("input[name*='[id]']");
    if (idInput) idInput.value = "";

    // Reset _destroy
    const destroyInput = clone.querySelector("input[name*='[_destroy]']");
    if (destroyInput) destroyInput.value = "0";

    // Update accordion IDs to avoid collisions
    const header = clone.querySelector(".accordion-header");
    const collapse = clone.querySelector(".accordion-collapse");
    const button = clone.querySelector(".accordion-button");

    if (header) header.id = `heading-section-${newIndex}`;
    if (collapse) {
      collapse.id = `collapse-section-${newIndex}`;
      collapse.setAttribute("aria-labelledby", `heading-section-${newIndex}`);
      // Start collapsed
      collapse.classList.remove("show");
    }
    if (button) {
      button.classList.add("collapsed");
      button.setAttribute("data-bs-target", `#collapse-section-${newIndex}`);
      button.setAttribute("aria-controls", `collapse-section-${newIndex}`);
      button.setAttribute("aria-expanded", "false");
    }

    // Clear parent-section-id on children container since this is a new record
    const childrenContainer = clone.querySelector(".children-container");
    if (childrenContainer) {
      childrenContainer.dataset.parentSectionId = "";
    }

    // Reset drag handle initialization so nested-sortable picks it up
    const handle = clone.querySelector("[data-nested-sortable-initialized]");
    if (handle) delete handle.dataset.nestedSortableInitialized;
  }

  // Remove any nested child sections from the clone — they belong to the original
  clearChildSections(clone) {
    const childrenContainer = clone.querySelector(".children-container");
    if (!childrenContainer) return;

    childrenContainer
      .querySelectorAll(".nested-attrs-field")
      .forEach((child) => child.remove());
  }
}
